import {useState, useMemo, useCallback} from 'react';
import {STALE_TIME_5_MIN} from '@common/constants/time';
import {trpc} from '@common-web/lib/trpc';

type ProductsQuery = ReturnType<typeof trpc.group.vbaMembers.products.useQuery>;

export type CatalogProduct = NonNullable<ProductsQuery['data']>['data'][number];

export function useProductSelection({groupId}: {groupId: string | undefined}) {
  const [initialProductId, setInitialProductIdRaw] = useState('');
  const [additionalProductIds, setAdditionalProductIds] = useState<string[]>([]);

  const productsQuery = trpc.group.vbaMembers.products.useQuery(
    {groupId: groupId!},
    {enabled: !!groupId, staleTime: STALE_TIME_5_MIN},
  );

  const productCatalog: CatalogProduct[] = productsQuery.data?.data ?? [];

  const setInitialProductId = useCallback(
    (id: string) => {
      setInitialProductIdRaw(id);
      setAdditionalProductIds((prev) => prev.filter((existing) => existing !== id));
    },
    [],
  );

  const remainingProducts = useMemo(
    () =>
      initialProductId
        ? productCatalog.filter((p) => p.id !== initialProductId)
        : productCatalog,
    [productCatalog, initialProductId],
  );

  const allSelectedProductIds = useMemo(() => {
    const ids: string[] = [];
    if (initialProductId) ids.push(initialProductId);
    ids.push(...additionalProductIds);
    return ids;
  }, [initialProductId, additionalProductIds]);

  const getProductName = useCallback(
    (id: string) => productCatalog.find((p) => p.id === id)?.name ?? id,
    [productCatalog],
  );

  const toggleAdditionalProduct = useCallback(
    (productId: string) => {
      setAdditionalProductIds((prev) =>
        prev.includes(productId) ? prev.filter((id) => id !== productId) : [...prev, productId],
      );
    },
    [],
  );

  return {
    additionalProductIds,
    allSelectedProductIds,
    getProductName,
    initialProductId,
    productCatalog,
    productsQuery,
    remainingProducts,
    setAdditionalProductIds,
    setInitialProductId,
    toggleAdditionalProduct,
  };
}
